import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { InputField } from "components/designV2/inputfield/inputfield";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { ColorV2 } from "config/colors";
import { createCompanyUser } from "services/firebase/firebase.service";
import firebase from "services/firebase/firebase.config";
import i18n from "services/translation/i18n";
import { useAuthStore } from "stores/authStore/authStore";
import { validateEmail } from "web-apps/company/utils/utils";
import {
  ActionsRow,
  FieldsWrapper,
  StyledForm,
  StyledTitle,
  TermsLink,
  TermsSection,
  TermsText,
  TermsTextWrapper,
} from "./signUp.styled";

const SIGN_UP_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.PositiveGreen,
  hoverBackgroundColor: ColorV2.SimflexityGreen,
  activeBackgroundColor: ColorV2.SimflexityGreen,
  disabledBackgroundColor: ColorV2.PositiveGreenLight,
  disabledTextColor: ColorV2.White,
  disabledBorderColor: "transparent",
};

const openExternalLink = (url: string): void => {
  window.open(url, "_blank", "noopener,noreferrer");
};

export const SignUp: React.FC = () => {
  const { t } = useTranslation();
  const requiredLabel = t("System.Required").toLowerCase();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const history = useHistory();
  const [, , authDispatch] = useAuthStore();
  const trimmedEmail = email.trim();

  const lang = i18n.language;
  const normalizedLang = lang.toLowerCase();
  const isSwedish =
    normalizedLang === "sv" ||
    normalizedLang === "se" ||
    normalizedLang.startsWith("sv-") ||
    normalizedLang.startsWith("se-");

  const validate = () => {
    if (
      trimmedEmail &&
      password &&
      repeatPassword &&
      !emailError &&
      !passwordError &&
      isChecked
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleSignUp = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (validate()) {
      setIsSubmitting(true);

      try {
        const res = await createCompanyUser(trimmedEmail, password);

        if (res.user) {
          authDispatch({
            type: "ACCOUNT_SIGN_IN",
            payload: {
              firestoreUser: res.user,
            },
          });
        }

        history.push("/company/registration");
      } catch (error) {
        const firebaseError = error as firebase.auth.Error;

        switch (firebaseError.code) {
          case "auth/invalid-email":
            setEmailError(t("RegisterCompany.InvalidEmail"));
            break;
          case "auth/email-already-in-use":
            setEmailError(t("RegisterCompany.EmailInUse"));
            break;
          case "auth/weak-password":
            setPasswordError(t("RegisterCompany.WeakPassword"));
            break;
          default:
            setPasswordError(t("RegisterCompany.SomethingWentWrong"));
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  useEffect(() => {
    if (!trimmedEmail) {
      setEmailError("");
      return;
    }

    if (validateEmail(trimmedEmail)) {
      setEmailError("");
    } else {
      setEmailError(t("RegisterCompany.InvalidEmail"));
    }
  }, [t, trimmedEmail]);

  useEffect(() => {
    if (password && password.length < 6) {
      setPasswordError(t("RegisterCompany.PasswordMustBe6Characters"));
      return;
    }

    if (password !== repeatPassword) {
      setPasswordError(t("RegisterCompany.PasswordDoesNotMatch"));
      return;
    }

    setPasswordError("");
  }, [password, repeatPassword, t]);

  return (
    <StyledForm onSubmit={handleSignUp}>
      <StyledTitle>{t("RegisterCompany.SignupTitle")}</StyledTitle>
      <FieldsWrapper>
        <InputField
          label={`${t("General.Email")} (${requiredLabel})`}
          type="email"
          value={email}
          placeholder={t("General.Email")}
          fullWidth
          hasError={Boolean(emailError)}
          errorText={emailError}
          onChange={setEmail}
          onClear={() => setEmail("")}
          disabled={isSubmitting}
        />
        <InputField
          label={`${t("General.Password")} (${requiredLabel})`}
          type="password"
          value={password}
          placeholder={t("General.Password")}
          fullWidth
          hasError={Boolean(passwordError)}
          errorText={passwordError}
          onChange={setPassword}
          onClear={() => setPassword("")}
          disabled={isSubmitting}
        />
        <InputField
          label={`${t("General.RepeatPassword")} (${requiredLabel})`}
          type="password"
          value={repeatPassword}
          placeholder={t("General.RepeatPassword")}
          fullWidth
          hasError={Boolean(passwordError)}
          errorText={passwordError}
          onChange={setRepeatPassword}
          onClear={() => setRepeatPassword("")}
          disabled={isSubmitting}
        />
      </FieldsWrapper>
      <TermsSection>
        <Checkbox
          checked={isChecked ? "checked" : "unchecked"}
          onChange={setIsChecked}
          disabled={isSubmitting}
        />
        <TermsTextWrapper>
          <TermsText>
            {t("RegisterCompany.CreatingAccountAgreeTermsOfService")}{" "}
            <TermsLink
              type="button"
              onClick={() => {
                openExternalLink(
                  isSwedish
                    ? "https://simflexity.com/sv/allmanna-villkor"
                    : "https://simflexity.com/en/terms",
                );
              }}
            >
              {t("RegisterCompany.TermsOfService")}
            </TermsLink>
            {t("RegisterCompany.TermsSeparator")}{" "}
            <TermsLink
              type="button"
              onClick={() => {
                openExternalLink(
                  isSwedish
                    ? "https://simflexity.com/sv/integritetspolicy"
                    : "https://simflexity.com/en/privacy",
                );
              }}
            >
              {t("RegisterCompany.PrivacyPolicy")}
            </TermsLink>
            {t("RegisterCompany.FinalTermsSeparator")}{" "}
            <TermsLink
              type="button"
              onClick={() => {
                openExternalLink(
                  isSwedish
                    ? "https://www.simflexity.com/sv/personuppgiftsbitradesavtal"
                    : "https://www.simflexity.com/en/data-processing-agreement",
                );
              }}
            >
              {t("RegisterCompany.DataProcessingAgreement")}
            </TermsLink>
          </TermsText>
        </TermsTextWrapper>
      </TermsSection>
      <ActionsRow>
        <SecondaryButton
          type="button"
          label={t("General.Cancel")}
          disabled={isSubmitting}
          onClick={() => history.push("./login")}
        />
        <PrimaryButton
          type="submit"
          label={t("RegisterCompany.SignUp")}
          disabled={!validate() || isSubmitting}
          isLoading={isSubmitting}
          styleOverrides={SIGN_UP_BUTTON_STYLES}
        />
      </ActionsRow>
    </StyledForm>
  );
};
